/** Range Planning "Menus" detail page (Demo Mode only). Tabbed layout follows the same
 * pattern as RangePlanningItems/ItemDetail.tsx and RecipeV2Detail.tsx. */

import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import { UserContext } from '../../components/shared/useUser';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { ArrowLeft } from '../../assets';
import useMenuDetail from './hooks/useMenuDetail';
import { MenuDetailTab, MenuStatus } from './types';
import OverviewTab from './components/OverviewTab';
import ServicePeriodsTab from './components/ServicePeriodsTab';
import MenuStructureTab from './components/MenuStructureTab';
import StandaloneItemsTab from './components/StandaloneItemsTab';
import SitesTab from './components/SitesTab';
import CostingTab from './components/CostingTab';
import AllergensAndDietaryTab from './components/AllergensAndDietaryTab';
import CarbonAndSustainabilityTab from './components/CarbonAndSustainabilityTab';
import VersionHistoryTab from './components/VersionHistoryTab';
import PublishingTab from './components/PublishingTab';
import AIChecksPanel from '../../components/shared/aiChecks/AIChecksPanel';
import { aiChecksClient } from '../../app/services/api/aiChecks/aiChecksEngine';
import { AICheckFinding } from '../../app/services/api/aiChecks/aiChecksTypes';

const statusColor: Record<MenuStatus, 'success' | 'warning' | 'info' | 'default'> = {
  [MenuStatus.Live]: 'success',
  [MenuStatus.Scheduled]: 'warning',
  [MenuStatus.Draft]: 'info',
  [MenuStatus.Archived]: 'default',
};

const MenuDetail = () => {
  const { t } = useTranslation('menus');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { hasPermissionTo } = useContext(UserContext);
  const { menu, recipeSummaries, isLoading, loadMenu, publish } = useMenuDetail();
  const [tabValue, setTabValue] = useState<MenuDetailTab>(MenuDetailTab.Overview);
  const [aiChecksFindings, setAiChecksFindings] = useState<AICheckFinding[]>([]);

  useEffect(() => {
    if (id) loadMenu(Number(id));
  }, [id, loadMenu]);

  useEffect(() => {
    if (id) aiChecksClient.getFindingsForMenu(Number(id)).then(setAiChecksFindings);
  }, [id]);

  if (isLoading || !menu) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/menus')}
          aria-label={t('backToMenus', 'Back to menus')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle>{menu.name}</PageTitle>
        <Chip label={t(`status.${menu.status}`, menu.status)}
          color={statusColor[menu.status]}
          size="small" />
        {hasPermissionTo(['MenusRead']) && (
          <Button
            variant="secondary"
            size="md"
            sx={{ ml: 'auto' }}
            onClick={() => navigate(`/menus/${menu.id}/public`)}
          >
            {t('viewPublicMenu', 'View Public Menu')}
          </Button>
        )}
      </Stack>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4 }}>
        {menu.description}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <AIChecksPanel findings={aiChecksFindings} />
      </Box>

      <Tabs value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: '-1px', borderBottom: 1, borderColor: 'divider', ml: 4, mr: 4, px: 2 }}>
        <Tab label={t('tabs.overview', 'Overview')}
          value={MenuDetailTab.Overview} />
        <Tab label={t('tabs.servicePeriods', 'Service Periods')}
          value={MenuDetailTab.ServicePeriods} />
        <Tab label={t('tabs.menuStructure', 'Menu Structure')}
          value={MenuDetailTab.MenuStructure} />
        <Tab label={t('tabs.standaloneItems', 'Standalone Items')}
          value={MenuDetailTab.StandaloneItems} />
        <Tab label={t('tabs.sites', 'Sites')}
          value={MenuDetailTab.Sites} />
        <Tab label={t('tabs.costing', 'Costing')}
          value={MenuDetailTab.Costing} />
        <Tab label={t('tabs.allergensAndDietary', 'Allergens & Dietary')}
          value={MenuDetailTab.AllergensAndDietary} />
        <Tab label={t('tabs.carbonAndSustainability', 'Carbon & Sustainability')}
          value={MenuDetailTab.CarbonAndSustainability} />
        <Tab label={t('tabs.versionHistory', 'Version History')}
          value={MenuDetailTab.VersionHistory} />
        <Tab label={t('tabs.publishing', 'Publishing')}
          value={MenuDetailTab.Publishing} />
      </Tabs>

      <Section style={{ mt: 0, p: 5 }}>
        {tabValue === MenuDetailTab.Overview && <OverviewTab menu={menu} />}
        {tabValue === MenuDetailTab.ServicePeriods && <ServicePeriodsTab menu={menu} />}
        {tabValue === MenuDetailTab.MenuStructure && <MenuStructureTab menu={menu}
          recipeSummaries={recipeSummaries} />}
        {tabValue === MenuDetailTab.StandaloneItems && <StandaloneItemsTab menu={menu} />}
        {tabValue === MenuDetailTab.Sites && <SitesTab menu={menu} />}
        {tabValue === MenuDetailTab.Costing && <CostingTab menu={menu}
          recipeSummaries={recipeSummaries} />}
        {tabValue === MenuDetailTab.AllergensAndDietary && <AllergensAndDietaryTab recipeSummaries={recipeSummaries} />}
        {tabValue === MenuDetailTab.CarbonAndSustainability && <CarbonAndSustainabilityTab recipeSummaries={recipeSummaries} />}
        {tabValue === MenuDetailTab.VersionHistory && <VersionHistoryTab menu={menu} />}
        {tabValue === MenuDetailTab.Publishing && <PublishingTab menu={menu}
          onPublish={() => id && publish(Number(id))} />}
      </Section>
    </PageArea>
  );
};

export default MenuDetail;
